import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useMemo, useState } from 'react';
import { useUsers } from '@Hooks/useUsers';
import {
  useAgentContacts,
  useAddContact,
  useRemoveContact,
} from '@Hooks/useAgentContacts';

const SELECT_CLASS =
  'border-input focus-visible:border-ring focus-visible:ring-ring/50 flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:ring-[3px]';

interface Props {
  soulId: string;
}

export default function AgentContactsCard({ soulId }: Props) {
  const { data: contacts, status } = useAgentContacts({ soulId });
  const { data: usersResponse } = useUsers({
    searchParams: { 'page[size]': 200 },
  });
  const add = useAddContact(soulId);
  const remove = useRemoveContact(soulId);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');

  const available = useMemo(() => {
    const taken = new Set((contacts ?? []).map((c) => c.userId));
    return (usersResponse?.data ?? []).filter((u: any) => !taken.has(u.id));
  }, [contacts, usersResponse]);

  const handleAdd = async () => {
    if (!selected) return;
    try {
      await add.mutateAsync(selected);
      setSelected('');
      setOpen(false);
      toastSuccess('Contact added.');
    } catch (e: any) {
      toastError(`Failed: ${e?.message ?? e}`);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await remove.mutateAsync(id);
      toastSuccess('Contact removed.');
    } catch (e: any) {
      toastError(`Remove failed: ${e?.message ?? e}`);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <Heading level="h3">Contacts</Heading>
          <p className="text-xs text-zinc-500">
            Who receives this agent's reports by email.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" disabled={!available.length}>
              Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Contact</DialogTitle>
            </DialogHeader>
            <select
              className={SELECT_CLASS}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Select a user…</option>
              {available.map((user: any) => (
                <option key={user.id} value={user.id}>
                  {user.name ? `${user.name} · ${user.email}` : user.email}
                </option>
              ))}
            </select>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="link">Cancel</Button>
              </DialogClose>
              <Button
                onClick={handleAdd}
                loading={add.isPending}
                disabled={!selected}
              >
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {status === 'pending' ? (
          <div className="py-4 text-sm text-zinc-500">Loading…</div>
        ) : !contacts?.length ? (
          <div className="py-4 text-sm text-zinc-500">
            No contacts yet — this agent's reports aren't emailed to anyone.
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {contact.user?.name ?? contact.user?.email ?? 'Unknown user'}
                  </div>
                  {contact.user?.name && (
                    <div className="truncate text-xs text-zinc-500">
                      {contact.user.email}
                    </div>
                  )}
                </div>
                <Button
                  variant="dangerLink"
                  size="sm"
                  onClick={() => handleRemove(contact.id)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
